import React from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  Image,
  Heading,
  VStack,
  HStack,
  Icon,
  useToast,
} from "@chakra-ui/react";
import {
  ExternalLinkIcon,
  ArrowBackIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import CloudIcon from "../../assets/images/cloud-footprint.png";
import Translate from "../LanguageTranslation/Translate";

const ProofOfImpact = ({ paymentData, cause, onBackToStep2 }) => {
  const toast = useToast();

  const handlePaymentClick = () => {
    if (paymentData?.paymentUrl) {
      // Open payment link in new tab
      window.open(paymentData.paymentUrl, "_blank");

      // Show success toast
      toast({
        title: "Payment Link Opened",
        description:
          "Payment page has been opened in a new tab. Complete your payment there.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (!paymentData) {
    return (
      <Box py={20} textAlign="center">
        <Heading size="lg" mb={0}>
          No payment data available
        </Heading>
        <Text mb={0}>Calculate carbon footprint to generate payment link</Text>
        <Button
          mt={4}
          colorScheme="customGreen"
          onClick={onBackToStep2}
          leftIcon={<ArrowBackIcon />}
        >
          Back to Calculator
        </Button>
      </Box>
    );
  }

  return (
    <Flex
      maxW={{ base: "90%", md: "90%", lg: "80%", xl: "80%" }}
      mx="auto"
      pt={12}
      pb={20}
      px={{ base: 4, md: 6 }}
      gap={8}
      direction={{ base: "column", md: "row" }}
    >
      {/* Left: Impact Summary */}
      <Box flex="2">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          {/* <Box textAlign="center">
            <Heading size="lg" color="green.600" mb={2}>
              <Translate text="Step 3: Proof of Impact" />
            </Heading>
            <Text color="gray.600" fontSize="lg">
              <Translate text="Complete your carbon offset contribution" />
            </Text>
          </Box> */}

          {/* Impact Summary Card */}
          <Box
            bg="white"
            p={6}
            borderRadius="lg"
            boxShadow="md"
            border="2px solid"
            borderColor="#286328"
          >
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="bold" color="#286328">
                  <Translate text="Your Carbon Impact" />
                </Text>
                <Icon as={CheckCircleIcon} color="#286328" boxSize={6} />
              </HStack>

              <Box>
                <Text fontSize="md" color="gray.600" mb={2}>
                  <Translate text="Trip Type" />
                </Text>
                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  textTransform="capitalize"
                  mb={0}
                >
                  {paymentData.tripType || "Travel"}
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.600" mb={2}>
                  <Translate text="Carbon Emissions" />
                </Text>
                <Text fontSize="lg" fontWeight="semibold" mb={0}>
                  {paymentData.emissions
                    ? (paymentData.emissions / 1000).toFixed(4)
                    : "0.0000"}{" "}
                  MT CO₂e
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.600" mb={2}>
                  <Translate text="Offset Cost" />
                </Text>
                <Text fontSize="lg" fontWeight="semibold" color="#286328" mb={0}>
                  {paymentData.amount} {paymentData.currency}
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.600" mb={2}>
                  <Translate text="Supporting Cause" />
                </Text>
                <Text fontSize="lg" fontWeight="semibold" mb={0}>
                  {paymentData.cause}
                </Text>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Box>

      {/* Right: Payment Action */}
      <Box flex="1">
        <Box
          bg="white"
          p={6}
          borderRadius="lg"
          boxShadow="md"
          minW={{ base: "full", md: "320px" }}
          position="sticky"
          top="20px"
        >
          {/* Cloud graphic */}
          <Box
            borderRadius="md"
            display="inline-flex"
            flexDir="column"
            alignItems="center"
            w="full"
            mb={6}
          >
            <Image
              src={CloudIcon}
              objectFit="contain"
              alt="Carbon cloud footprint"
              h="80px"
              mb={2}
            />
          </Box>

          <Heading fontSize="lg" mb={4} textAlign="center">
            <Translate text="Ready to Offset?" />
          </Heading>

          <Text fontSize="sm" color="gray.600" mb={6} textAlign="center">
            <Translate text="Click below to complete your carbon offset contribution and receive your Proof of Impact." />
          </Text>

          {/* Payment Button */}
          <Button
            w="full"
            bg="#286328"
            color="white"
            _hover={{ bg: "#286328" }}
            borderRadius="lg"
            fontWeight="bold"
            py={6}
            onClick={handlePaymentClick}
            leftIcon={<ExternalLinkIcon />}
            size="lg"
          >
            <Translate text="Complete Payment" />
          </Button>

          {/* Back Button */}
          <Button
            w="full"
            variant="outline"
            mt={4}
            onClick={onBackToStep2}
            leftIcon={<ArrowBackIcon />}
          >
            <Translate text="Back to Calculator" />
          </Button>

          {/* Security Note */}
          <Text fontSize="xs" color="gray.500" mt={6} textAlign="center">
            <Translate text="Your payment will be processed securely through our trusted payment partner." />
          </Text>
        </Box>
      </Box>
    </Flex>
  );
};

export default ProofOfImpact;
